import { useRef, useState } from "react";

import { useMutation, useQueryClient } from "@tanstack/react-query";

import { Button, Icon, Modal, SearchSelect } from "@/components/common";
import { useAuth } from "@/auth/useAuth";
import { extractErrorMessage } from "@/services/axiosConfig";
import { invitationService } from "@/services/invitationService";
import { organizationService } from "@/services/organizationService";
import { useCnpjLookup, useCreateOrganization } from "@/hooks/useOrganizations";
import { useMembers } from "@/hooks/useInvitations";
import { formatCnpj, formatCpf } from "@/utils/fmt";
import type { OrganizationInvitationCreatePayload } from "@/types/invitation";
import type { OrganizationAddress } from "@/types/organization";
import "./OrgCreateModal.css";

type Mode = "link" | "manual";
type DocType = "cnpj" | "cpf";

interface SelectOption {
  id: string;
  label: string;
  sub?: string;
}

interface TdConfigProps {
  selectedWsId: string;
  onWorkspaceChange: (value: string) => void;
  wsOptions: SelectOption[];
  canPickWorkspace: boolean;
  respId: string;
  onRespChange: (value: string) => void;
  respOptions: SelectOption[];
}

/** Configuração TD (workspace-dono + responsável). Componente de topo de módulo:
 * declarar dentro do render do pai remontaria a subárvore a cada keystroke. */
function TdConfig({
  selectedWsId,
  onWorkspaceChange,
  wsOptions,
  canPickWorkspace,
  respId,
  onRespChange,
  respOptions,
}: TdConfigProps) {
  return (
    <>
      <div className="form-field">
        <span className="orgc-eyebrow">Workspace-dono</span>
        <SearchSelect
          value={selectedWsId}
          onChange={onWorkspaceChange}
          options={wsOptions}
          placeholder="Selecione o workspace…"
          disabled={!canPickWorkspace}
        />
        <p className="orgc-hint">
          A organização sempre pertence a um workspace. O cliente vê isso como banner fixo.
        </p>
      </div>
      <div className="form-field">
        <span className="orgc-eyebrow">Responsável</span>
        <SearchSelect
          value={respId}
          onChange={onRespChange}
          options={respOptions}
          placeholder="Selecione o responsável…"
          emptyLabel="Nenhum membro encontrado"
        />
      </div>
    </>
  );
}

interface Props {
  workspaceId: number;
  workspaceName?: string;
  onClose: () => void;
}

/** "Criar nova organização": enviar link (convite) ou cadastrar manualmente
 * (2 colunas: Configuração TD | Etapa 1 dados da org). Fiel ao design. */
export function OrgCreateModal({ workspaceId, workspaceName, onClose }: Props) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const cnpjLookup = useCnpjLookup();
  const lastLookedUp = useRef<string>("");

  const [mode, setMode] = useState<Mode>("link");

  // Workspace-dono (admin pode trocar) + responsável (membro do workspace).
  const workspaces = user?.manageableWorkspaces ?? [];
  const [selectedWsId, setSelectedWsId] = useState<string>(String(workspaceId));
  const wsId = Number(selectedWsId);
  const { data: members = [] } = useMembers(wsId);
  const [respId, setRespId] = useState<string>(user ? String(user.id) : "");

  const createOrg = useCreateOrganization(wsId);
  const createOrgInvite = useMutation({
    mutationFn: (payload: OrganizationInvitationCreatePayload) =>
      invitationService.createOrg(wsId, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["invitations", "org", wsId] }),
  });

  // Link mode
  const [email, setEmail] = useState("");
  const [link, setLink] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  // Manual mode
  const [docType, setDocType] = useState<DocType>("cnpj");
  const [nomeOrg, setNomeOrg] = useState("");
  const [cnpj, setCnpj] = useState("");
  const [cpf, setCpf] = useState("");
  const [razao, setRazao] = useState("");
  const [nomePf, setNomePf] = useState("");
  const [respCpf, setRespCpf] = useState("");
  const [respUsarWs, setRespUsarWs] = useState(true);
  const [createdName, setCreatedName] = useState<string | null>(null);
  // Endereço do lookup (não exibido; persistido ao criar, editável no detalhe).
  const [addr, setAddr] = useState<OrganizationAddress | null>(null);

  const isPj = docType === "cnpj";
  const selectedWs = workspaces.find((w) => String(w.id) === selectedWsId);
  const wsName = selectedWs?.name ?? workspaceName ?? "este workspace";

  const wsOptions = workspaces.map((w) => ({
    id: String(w.id),
    label: w.name,
    sub: w.is_platform_tenant ? "Workspace principal" : "Cliente",
  }));
  const respOptions = members.map((m) => ({
    id: String(m.user_id),
    label: m.user_name || m.user_email_masked,
    sub: m.role_name,
  }));
  // Admin (platform ou >1 workspace) pode trocar o workspace-dono.
  const canPickWorkspace = (user?.isPlatformAdmin ?? false) || workspaces.length > 1;

  const busy = createOrg.isPending || createOrgInvite.isPending;
  const error = createOrg.isError
    ? extractErrorMessage(createOrg.error)
    : createOrgInvite.isError
      ? extractErrorMessage(createOrgInvite.error)
      : null;

  function onWorkspaceChange(value: string) {
    setSelectedWsId(value);
    setRespId(user ? String(user.id) : "");
  }

  function setDoc(next: DocType) {
    setDocType(next);
    if (next === "cnpj") {
      setCpf("");
      setNomePf("");
    } else {
      setCnpj("");
      setRazao("");
    }
  }

  function onCnpjChange(raw: string) {
    setCnpj(formatCnpj(raw));
    const digits = raw.replace(/\D/g, "").slice(0, 14);
    if (digits.length === 14 && digits !== lastLookedUp.current) {
      lastLookedUp.current = digits;
      cnpjLookup.mutate(digits, {
        onSuccess: (d) => {
          if (d.razao_social) setRazao(d.razao_social);
          setAddr({
            street: d.logradouro,
            number: d.numero,
            complement: d.complemento,
            neighborhood: d.bairro,
            city: d.cidade,
            state: d.uf,
            zip_code: d.cep,
          });
        },
      });
    }
  }

  function generateLink() {
    createOrgInvite.mutate(
      {
        email: email.trim(),
        organization_name_suggestion: nomeOrg.trim() || undefined,
      },
      { onSuccess: (created) => setLink(created.accept_url) },
    );
  }

  function createManual() {
    createOrg.mutate(
      {
        cnpj: cnpj.replace(/\D/g, ""),
        legal_name: razao.trim(),
        nickname: nomeOrg.trim() || undefined,
        legal_rep_cpf: respUsarWs ? null : respCpf.replace(/\D/g, "") || null,
        // Payload alinhado ao create canônico (OrgFormModal): a org herda o
        // operator_type/td_share do workspace por padrão. Enviar null explícito
        // mantém a mesma forma de payload no mesmo endpoint (evita divergência
        // silenciosa — ARCH-002).
        operator_type: null,
        default_td_share_pct: null,
      },
      {
        onSuccess: (created) => {
          setCreatedName(nomeOrg.trim() || razao.trim());
          // Persiste o endereço do lookup (editável depois no detalhe da org).
          if (addr && Object.values(addr).some(Boolean)) {
            void organizationService.setAddress(wsId, created.id, addr);
          }
        },
      },
    );
  }

  function copyLink() {
    if (link) void navigator.clipboard?.writeText(link).catch(() => undefined);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }

  const canSendLink = !!email.trim();
  // Manual: só CNPJ é suportado no create direto (org PF depende de backend).
  const canCreateManual = isPj && cnpj.replace(/\D/g, "").length === 14 && !!razao.trim() && !!nomeOrg.trim();

  const tdConfig = (
    <TdConfig
      selectedWsId={selectedWsId}
      onWorkspaceChange={onWorkspaceChange}
      wsOptions={wsOptions}
      canPickWorkspace={canPickWorkspace}
      respId={respId}
      onRespChange={setRespId}
      respOptions={respOptions}
    />
  );

  // ── Sucesso: link ──────────────────────────────────────────────────────
  if (link) {
    return (
      <Modal
        title="Link de cadastro gerado"
        subtitle="Envie ao cliente — validade 7 dias, uso único."
        size="md"
        onClose={onClose}
        footer={<Button variant="primary" onClick={onClose}>Concluir</Button>}
      >
        <div className="orgc-link-box">
          <code>{link}</code>
          <Button variant="ghost" size="sm" iconLeft={copied ? "check" : "copy"} onClick={copyLink}>
            {copied ? "Copiado" : "Copiar"}
          </Button>
        </div>
        <p className="orgc-hint">
          Enviado a <strong>{email}</strong> · vincula a organização ao workspace{" "}
          <strong>{wsName}</strong>.
        </p>
      </Modal>
    );
  }

  // ── Sucesso: manual ────────────────────────────────────────────────────
  if (createdName) {
    return (
      <Modal
        title="Organização criada"
        subtitle="Certificado e contatos ficam pendentes para completar nas configurações."
        size="sm"
        onClose={onClose}
        footer={<Button variant="primary" onClick={onClose}>Concluir</Button>}
      >
        <div className="orgc-created">
          <span className="orgc-created-icon"><Icon name="check" size={22} /></span>
          <strong>{createdName}</strong> foi criada no workspace {wsName}.
        </div>
      </Modal>
    );
  }

  // ── Formulário ──────────────────────────────────────────────────────────
  return (
    <Modal
      title="Criar nova organização"
      subtitle={`Escolha como o cliente vai preencher o cadastro. A org será criada dentro do workspace ${wsName}.`}
      size={mode === "manual" ? "lg" : "md"}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancelar</Button>
          {mode === "link" ? (
            <Button variant="primary" iconLeft="send" disabled={!canSendLink || busy} onClick={generateLink}>
              {busy ? "Gerando…" : "Gerar link"}
            </Button>
          ) : (
            <Button variant="primary" iconLeft="check" disabled={!canCreateManual || busy} onClick={createManual}>
              {busy ? "Criando…" : "Criar organização"}
            </Button>
          )}
        </>
      }
    >
      {error && <div className="form-error" style={{ marginBottom: 12 }}>{error}</div>}

      <div className="orgc-modes">
        <button type="button" className={`orgc-mode ${mode === "link" ? "is-on" : ""}`} onClick={() => setMode("link")}>
          <span className="orgc-mode-icon"><Icon name="send" size={16} /></span>
          <span className="orgc-mode-tag">Recomendado</span>
          <span className="orgc-mode-title">Enviar link</span>
          <span className="orgc-mode-sub">O cliente acessa o link e preenche. Cria a org ao final da Etapa 1.</span>
        </button>
        <button type="button" className={`orgc-mode ${mode === "manual" ? "is-on" : ""}`} onClick={() => setMode("manual")}>
          <span className="orgc-mode-icon"><Icon name="plus" size={16} /></span>
          <span className="orgc-mode-title">Cadastrar manualmente</span>
          <span className="orgc-mode-sub">Você preenche a Etapa 1. Certificado e contatos ficam pendentes.</span>
        </button>
      </div>

      {mode === "link" ? (
        <div className="orgc-fields">
          {tdConfig}
          <label className="form-field">
            <span className="orgc-eyebrow">Enviar para</span>
            <input
              className="form-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="cliente@empresa.com.br"
              autoFocus
            />
            <p className="orgc-hint">O destinatário cadastra a org e ela aparece no workspace dele.</p>
          </label>
          <label className="form-field">
            <span className="orgc-eyebrow">Nome da organização (opcional)</span>
            <input
              className="form-input"
              maxLength={120}
              value={nomeOrg}
              onChange={(e) => setNomeOrg(e.target.value)}
              placeholder="Ex: BRF Holdings"
            />
            <p className="orgc-hint">Sugestão de nome que aparece pré-preenchida no cadastro do cliente.</p>
          </label>
        </div>
      ) : (
        <div className="orgc-manual-grid">
          <div className="orgc-manual-col">
            <div className="orgc-manual-col-head">
              <span className="orgc-eyebrow">Configuração TD</span>
              <span className="orgc-col-note">Workspace dono · só admin TD edita</span>
            </div>
            <div className="orgc-fields">
              {tdConfig}
            </div>
          </div>

          <div className="orgc-manual-col">
            <div className="orgc-manual-col-head">
              <span className="orgc-eyebrow">Etapa 1 · Dados da org</span>
              <span className="orgc-col-note">Certificado e contatos pendentes</span>
            </div>
            <div className="orgc-fields">
              <div className="form-field">
                <span className="orgc-eyebrow">Tipo de cadastro</span>
                <div className="emp-doc-toggle emp-doc-toggle-lg" role="tablist">
                  <button type="button" className={`emp-doc-tab ${isPj ? "is-on" : ""}`} onClick={() => setDoc("cnpj")}>
                    <Icon name="building" size={13} /> CNPJ <span className="emp-doc-tab-tag">Pessoa Jurídica</span>
                  </button>
                  <button type="button" className={`emp-doc-tab ${!isPj ? "is-on" : ""}`} onClick={() => setDoc("cpf")}>
                    <Icon name="user" size={13} /> CPF <span className="emp-doc-tab-tag">Pessoa Física</span>
                  </button>
                </div>
              </div>

              <label className="form-field">
                <span className="orgc-eyebrow">Nome da organização</span>
                <input
                  className="form-input"
                  maxLength={120}
                  value={nomeOrg}
                  onChange={(e) => setNomeOrg(e.target.value)}
                  placeholder={isPj ? "Ex: BRF Holdings" : "Ex: Escritório João Silva"}
                />
              </label>

              {isPj ? (
                <>
                  <label className="form-field">
                    <span className="orgc-eyebrow">CNPJ da controladora {cnpjLookup.isPending && "· buscando…"}</span>
                    <input
                      className="form-input"
                      inputMode="numeric"
                      maxLength={18}
                      value={cnpj}
                      onChange={(e) => onCnpjChange(e.target.value)}
                      placeholder="00.000.000/0001-00"
                    />
                  </label>
                  <label className="form-field">
                    <span className="orgc-eyebrow">Razão social</span>
                    <input className="form-input" maxLength={200} value={razao} onChange={(e) => setRazao(e.target.value)} placeholder="—" />
                  </label>
                  <div className="form-field">
                    <span className="orgc-eyebrow">CPF do responsável</span>
                    <input
                      className="form-input"
                      inputMode="numeric"
                      maxLength={14}
                      value={respUsarWs ? "••• mesmo do workspace" : respCpf}
                      disabled={respUsarWs}
                      onChange={(e) => setRespCpf(formatCpf(e.target.value))}
                      placeholder="000.000.000-00"
                    />
                    <label className="orgc-check">
                      <span
                        className={`onb-checkbox-box ${respUsarWs ? "is-on" : ""}`}
                        onClick={() => setRespUsarWs(!respUsarWs)}
                      >
                        {respUsarWs && <Icon name="check" size={10} stroke={3} />}
                      </span>
                      <span>Usar o mesmo responsável legal do workspace</span>
                    </label>
                  </div>
                  <div className="orgc-note">
                    <Icon name="sparkles" size={13} /> Após criar, anexe o{" "}
                    <strong>certificado digital</strong> pra que as empresas do grupo sejam criadas
                    automaticamente.
                  </div>
                </>
              ) : (
                <>
                  <label className="form-field">
                    <span className="orgc-eyebrow">CPF</span>
                    <input className="form-input" inputMode="numeric" maxLength={14} value={cpf} onChange={(e) => setCpf(formatCpf(e.target.value))} placeholder="000.000.000-00" />
                  </label>
                  <label className="form-field">
                    <span className="orgc-eyebrow">Nome completo</span>
                    <input className="form-input" maxLength={120} value={nomePf} onChange={(e) => setNomePf(e.target.value)} placeholder="Nome conforme CPF" />
                  </label>
                  <div className="orgc-note">
                    <Icon name="alert-triangle" size={13} /> Organização PF pelo manual em breve —
                    use o <strong>link</strong> para cadastrar pessoa física.
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
}
